import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_ALIGNMENT,
  ruleWrapMode,
  GRID_SETTING_KEYS,
  MAX_LINES,
  MIN_LINES,
  flexClass,
  loadGridSettings,
  normalizeAlignment,
  saveGridSetting,
  useGridConfig,
  type CellAlign,
  type CellRule,
  type CellType,
  type CellWrapMode,
  type GridAlignment,
} from '@/shared/components/Grid';
import { useI18n } from '@/shared/i18n/useT';
import { useOrg } from '@/shared/org';
import { Icon, type IconName } from '@/shared/components/ui/Icon';
import type { Json } from '@/lib/database.types';
import { SheetPreview } from './SheetPreview';

export function GridAlignmentSection() {
  const { t } = useI18n();
  const org = useOrg();

  const [valores, setValores] = useState<GridAlignment>(DEFAULT_ALIGNMENT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = org.status === 'ready' ? org.orgId : null;
  // `canEditShared` is the client-side mirror of the `grid_settings_write`
  // policy for an organization-wide row: owner and developer outright, anyone
  // else through a `sheets` grant of edit_format or better. `isAdmin` was both
  // too narrow and too wide — it shut out developers the database accepts, and
  // enabled the controls for admins whose writes RLS then refused, which reads
  // as the app silently ignoring you.
  const editable = useGridConfig().canEditShared;

  const TIPOS_CELDA: { tipo: CellType | 'header'; label: string; ayuda: string }[] = [
    {
      tipo: 'header',
      label: t('settings.grid.cellType.header'),
      ayuda: t('settings.grid.cellType.headerHint'),
    },
    {
      tipo: 'text',
      label: t('settings.grid.cellType.text'),
      ayuda: t('settings.grid.cellType.textHint'),
    },
    {
      tipo: 'chip',
      label: t('settings.grid.cellType.chip'),
      ayuda: t('settings.grid.cellType.chipHint'),
    },
    {
      tipo: 'number',
      label: t('settings.grid.cellType.number'),
      ayuda: t('settings.grid.cellType.numberHint'),
    },
    {
      tipo: 'date',
      label: t('settings.grid.cellType.date'),
      ayuda: t('settings.grid.cellType.dateHint'),
    },
  ];

  const ALINEACIONES: { valor: CellAlign; label: string; renglones: string[] }[] = [
    { valor: 'left', label: t('settings.grid.align.left'), renglones: ['w-5', 'w-3', 'w-4'] },
    { valor: 'center', label: t('settings.grid.align.center'), renglones: ['w-5', 'w-3', 'w-4'] },
    { valor: 'right', label: t('settings.grid.align.right'), renglones: ['w-5', 'w-3', 'w-4'] },
  ];

  const AJUSTES: { valor: CellWrapMode; icon: IconName; label: string }[] = [
    { valor: 'wrap', icon: 'wrapText', label: t('grid.wrapText') },
    { valor: 'clip', icon: 'clipText', label: t('grid.clipText') },
    { valor: 'shrink', icon: 'shrinkText', label: t('grid.shrinkText') },
    { valor: 'expand', icon: 'expandText', label: t('grid.expandText') },
    { valor: 'overflow', icon: 'overflowText', label: t('grid.overflowText') },
  ];

  useEffect(() => {
    if (!orgId) {
      setValores(DEFAULT_ALIGNMENT);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);

    void (async () => {
      try {
        const settings = await loadGridSettings(orgId);
        if (!active) return;
        setValores(normalizeAlignment(settings[GRID_SETTING_KEYS.alignment]));
      } catch {
        if (active) setError(t('settings.grid.loadError'));
      }
      if (active) setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [orgId, t]);

  const setRegla = (tipo: CellType | 'header', cambio: Partial<CellRule>) => {
    // El guardado va fuera del updater: StrictMode lo invoca dos veces y escribía dos veces.
    const next = { ...valores, [tipo]: { ...valores[tipo], ...cambio } };
    setValores(next);
    void guardar(next);
  };

  const guardar = useCallback(async (valoresAGuardar: GridAlignment) => {
    if (!orgId) return;
    setError(null);
    const failure = await saveGridSetting(
      orgId,
      GRID_SETTING_KEYS.alignment,
      valoresAGuardar as unknown as Json,
    );
    if (failure) setError(t('settings.grid.saveError'));
  }, [orgId, t]);

  const restaurarDefaults = useCallback(async () => {
    setValores(DEFAULT_ALIGNMENT);
    void guardar(DEFAULT_ALIGNMENT);
  }, [guardar]);

  if (org.status !== 'ready' || loading) {
    return <p className="text-sm text-muted">{t('common.loading')}</p>;
  }

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-extrabold text-fg">{t('settings.grid.alignmentPage.title')}</h1>
        <p className="mt-1 text-sm text-muted">
          {t('settings.grid.alignmentPage.description')}
        </p>
      </div>

      {error && <div className="rounded-xl bg-danger/10 p-3 text-sm text-danger">{error}</div>}

      <div className="space-y-3">
        {TIPOS_CELDA.map(({ tipo, label, ayuda }) => {
          const regla = valores[tipo];
          const modo = ruleWrapMode(regla, tipo === 'header' ? 'text' : tipo);
          return (
            <div
              key={tipo}
              className="grid gap-3 rounded-xl border border-border bg-surface p-4 sm:grid-cols-[1fr_auto_auto_9rem] sm:items-center"
            >
              <div className="min-w-0">
                <p className="text-sm font-bold text-fg">{label}</p>
                <p className="text-xs text-muted">{ayuda}</p>
              </div>

              {/* Segmented alignment group */}
              <div className="flex overflow-hidden rounded-lg border border-border">
                {ALINEACIONES.map((a) => {
                  const activo = regla.align === a.valor;
                  return (
                    <button
                      key={a.valor}
                      type="button"
                      disabled={!editable}
                      title={a.label}
                      aria-label={a.label}
                      aria-pressed={activo}
                      onClick={() => setRegla(tipo, { align: a.valor })}
                      className={`flex h-10 w-10 flex-col items-center justify-center gap-1 border-r border-border last:border-r-0 transition-colors disabled:opacity-60 ${
                        activo
                          ? 'bg-accent/10 text-accent font-bold'
                          : 'text-muted hover:bg-surface-3 hover:text-fg'
                      }`}
                    >
                      <span className={`flex w-5 flex-col gap-[3px] ${flexClass({ ...regla, align: a.valor })}`}>
                        {a.renglones.map((ancho, i) => (
                          <span
                            key={i}
                            className={`block h-[2px] rounded-full bg-current ${ancho} ${
                              a.valor === 'center'
                                ? 'self-center'
                                : a.valor === 'right'
                                  ? 'self-end'
                                  : 'self-start'
                            }`}
                          />
                        ))}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Ajuste del texto: los mismos botones que la barra de formato */}
              <div className="flex overflow-hidden rounded-lg border border-border" role="group" aria-label={t('grid.wrapLabel')}>
                {AJUSTES.map((a) => {
                  const activo = modo === a.valor;
                  return (
                    <button
                      key={a.valor}
                      type="button"
                      disabled={!editable}
                      title={a.label}
                      aria-label={a.label}
                      aria-pressed={activo}
                      onClick={() => setRegla(tipo, { wrapMode: a.valor, wrap: a.valor === 'wrap' })}
                      className={`flex h-10 w-10 items-center justify-center border-r border-border last:border-r-0 transition-colors disabled:opacity-60 ${
                        activo ? 'bg-accent/10 text-accent' : 'text-muted hover:bg-surface-3 hover:text-fg'
                      }`}
                    >
                      <Icon name={a.icon} className="h-4 w-4" />
                    </button>
                  );
                })}
              </div>

              {/* Líneas máx: solo cuando el texto se ajusta en renglones */}
              {modo === 'wrap' ? <label className="flex items-center gap-2 text-sm text-fg">
                <span className="text-fg">{t('settings.grid.lines')}</span>
                <input
                  type="number"
                  min={MIN_LINES}
                  max={MAX_LINES}
                  value={regla.lines}
                  disabled={!editable}
                  onChange={(e) => setValores((v) => ({ ...v, [tipo]: { ...v[tipo], lines: Number(e.target.value) } }))}
                  onBlur={(e) =>
                    setRegla(tipo, {
                      lines: Math.min(
                        MAX_LINES,
                        Math.max(MIN_LINES, Math.round(Number(e.target.value) || MIN_LINES)),
                      ),
                    })
                  }
                  className="h-10 w-16 rounded-full border border-border bg-surface-2 px-2 text-sm text-fg disabled:opacity-40"
                />
              </label> : <span aria-hidden="true" />}
            </div>
          );
        })}
      </div>

      <div>
        <h2 className="mb-2 text-sm font-bold text-muted">{t('settings.grid.preview')}</h2>
        <SheetPreview alineacion={valores} />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={!editable}
          onClick={() => void restaurarDefaults()}
          className="rounded-full border border-border bg-surface px-4 py-2.5 text-sm font-bold text-fg transition-colors hover:bg-surface-3 disabled:opacity-60"
        >
          {t('settings.grid.restoreDefaults')}
        </button>
      </div>

      {!editable && <p className="text-sm text-muted">{t('settings.org.readOnly')}</p>}
    </section>
  );
}
